import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  H3,
  RegularTextSmall,
} from "../../../../components/Typography/Typography";
import { DownloadIcon } from "../../../../components/Icons/Icons";
import {
  PrimaryButtonLargeV2,
  SecondaryButtonMediumV2,
  TertiaryButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import { useHistory } from "react-router-dom";
import { useRoutePath } from "../../../../util/Routing";
import axios from "axios";
import { createDownloadLink, useStoreState } from "../../../../util/util";
import { spacingTokens } from "../../../../designTokens";
import { useMdmEntityDisplayNames } from "../../../../util/mdmEntityDisplayNames";
import { BulkUploadCustomerType } from "../../../../types/types.MDM";
import { bulkUploadEntityType } from "./BulkUploadCustomers";

const DownloadStageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_5xl")};
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

export const DownloadStage = ({
  updateStage,
  customer_type,
}: {
  updateStage: (stage: "download" | "upload") => void;
  customer_type: BulkUploadCustomerType;
}) => {
  const { t } = useTranslation();
  const { storefront_id, tenant_id } = useStoreState();
  const { getLabel } = useMdmEntityDisplayNames();
  const { adminPath } = useRoutePath();
  const history = useHistory();
  const listPath = customer_type === "customers" ? "tenants" : "distributors";
  const entityType = bulkUploadEntityType(customer_type);
  const singular = getLabel(entityType, "singular");
  const plural = getLabel(entityType, "plural");

  const handleDownload = async () => {
    const { data } = await axios.get(
      `/v2/storefronts/${storefront_id}/tenants/${tenant_id}/bulk_download`,
      { responseType: "text" }
    );
    const link: HTMLAnchorElement = createDownloadLink({
      name: `${customer_type}.csv`,
      type: "text/csv",
      data,
    });
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
    updateStage("upload");
  };

  const goBack = () => {
    history.push(`${adminPath}/${listPath}`);
  };

  return (
    <>
      <DownloadStageContainer>
        <TextSection>
          <H3 style={{ margin: 0 }}>{t("Download CSV")}</H3>
          <RegularTextSmall
            style={{ margin: `0 0 ${spacingTokens.spacing_xl}` }}
          >
            {t(
              "The following CSV files contains the headers of required data for {{entity}} creation. Download the file, update with {{entities}} information and upload.",
              { entity: singular, entities: plural }
            )}
          </RegularTextSmall>
          <RegularTextSmall style={{ margin: 0 }}>
            {t(
              "Please do not modify the first rows containing the headers. We also recommend keeping the same file name."
            )}
          </RegularTextSmall>
        </TextSection>
        <SecondaryButtonMediumV2
          style={{ width: "max-content" }}
          onClick={handleDownload}
        >
          <DownloadIcon width={16} height={16} />
          {t("Download CSV")}
        </SecondaryButtonMediumV2>
        <div style={{ display: "flex", gap: spacingTokens.spacing_xl }}>
          <TertiaryButtonLargeV2 onClick={goBack}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 onClick={() => updateStage("upload")}>
            {t("Continue")}
          </PrimaryButtonLargeV2>
        </div>
      </DownloadStageContainer>
    </>
  );
};
